import { useMemo } from "react";
import ModalShell from "../../../../_components/modal/ModalShell";
import ConfirmDialog from "../../../../_components/modal/ConfirmDialog";
import DynamicForm from "../../../../_components/form/DynamicForm";
import Button from "../../../../_components/ui/Button";
import { useUsageSourceFormState } from "./hooks/useUsageSourceFormState";
import ApiLinkExamplePanel from "./components/ApiLinkExamplePanel";
import "./_usage-source-form-modal.scss";

/**
 * UI Layer - presentational composition only, mirrors
 * _features/billingUnits/modals/BillingUnitFormModal/index.jsx's shape,
 * extended with the two pieces unique to Usage Sources: the API-LINK
 * example panel and the Validate/Re-Validate button. Every business
 * decision lives in useUsageSourceFormState/useUsageSourceValidation;
 * this file just wires their outputs into JSX.
 */
const UsageSourceFormModal = ({ isOpen, onClose, rowData, mode, onSuccess, refetchFilters }) => {
  const {
    control,
    schema,
    formData,
    errors,
    handleChange,
    handleBlur,
    showCloseConfirmation,
    isSubmitting,
    attemptClose,
    confirmDiscard,
    cancelDiscard,
    onSubmit,
    originSourceValue,
    shouldShowValidationButton,
    isReValidate,
    isValidating,
    handleValidate,
  } = useUsageSourceFormState({ mode, rowData, isOpen, onClose, onSuccess, refetchFilters });

  const description = useMemo(
    () =>
      mode === "edit"
        ? "Updates this source's connection details."
        : "Connects a data source that feeds customer consumption events into Kribana.",
    [mode]
  );

  return (
    <>
      <ModalShell
        isOpen={isOpen}
        onRequestClose={attemptClose}
        width="460px"
        height="100vh"
        center={false}
        right={0}
        className="usage-source-form-modal--right-docked"
        title={mode === "edit" ? "Edit usage source" : "New usage source"}
        description={description}
        isSubmitting={isSubmitting}
        footerButtons={[
          { children: "Close", onClick: attemptClose, variant: "outlined" },
          {
            children: mode === "edit" ? "Update" : "Create",
            onClick: onSubmit,
            variant: "contained",
            loading: isSubmitting,
          },
        ]}
      >
        <form onSubmit={onSubmit} className="usage-source-form-modal__scroll">
          <DynamicForm
            schema={schema}
            control={control}
            formData={formData}
            errors={errors}
            handleChange={handleChange}
            handleBlur={handleBlur}
            mode={mode}
            disabled={isSubmitting}
          />

          {originSourceValue === "API LINK" && <ApiLinkExamplePanel />}

          {shouldShowValidationButton && (
            <div className="usage-source-form-modal__validate">
              <Button
                variant="contained"
                onClick={handleValidate}
                loading={isValidating}
                disabled={isValidating || isSubmitting}
              >
                {mode === "edit" && isReValidate ? "Re-Validate" : "Validate"}
              </Button>
            </div>
          )}
        </form>
      </ModalShell>

      <ConfirmDialog
        isOpen={showCloseConfirmation}
        title="Discard unsaved changes?"
        description="You have unsaved changes. Are you sure you want to close without saving?"
        cancelText="Keep editing"
        confirmText="Discard changes"
        onCancel={cancelDiscard}
        onConfirm={confirmDiscard}
      />
    </>
  );
};

export default UsageSourceFormModal;
